import { ArticleRounded as ArticleRoundedIcon, MenuBookOutlined as MenuBookOutlinedIcon } from '@mui/icons-material';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { Fragment, useContext, useState } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { PublicationHistoryItem } from '~/shared/data-contract/publication-history';

import { CardIconCaption } from '../../../../design/components/cards/CardIconCaption';
import { InlineLoadingSpinner } from '../../../../design/components/LoadingSpinner';
import { usePublicationHistoryQuery } from '../../../query-hooks/users/publication-history';
import { useUserHistoryOptionsQuery } from '../../../query-hooks/users/user-history';
import { useScrollToLocationHash } from '../../../use-scroll-to-location-hash';
import { createUserPublicationHistoryUrl, editUserPublicationHistoryUrl } from '../constants';
import { AddButton } from '../user-profile/common/ActionButtons';
import { CardPlaceholder } from './common/CardPlaceholder';
import { HistoryItem } from './common/HistoryItem';
import { HistoryItemListWrapper } from './common/HistoryItemListWrapper';
import { ShowAllButton } from './common/ShowAllButton';

const PublicationHistoryCaption: FunctionComponent = () => {
    const { translate } = useContext(ApplicationContext);

    return (
        <CardIconCaption
            color="publication.main"
            icon={MenuBookOutlinedIcon}
            text={translate('pages.user-publication-history.caption')}
        />
    );
};

type PublicationHistoryContentProps = {
    readonly canUserAddPublicationHistory?: boolean;
    readonly canUserEditPublicationHistory?: boolean;
    readonly publicationHistory?: ReadonlyArray<PublicationHistoryItem>;
    readonly userId?: string;
};

export const PublicationHistoryContent: FunctionComponent<PublicationHistoryContentProps> = ({
    canUserAddPublicationHistory = false,
    canUserEditPublicationHistory = false,
    publicationHistory = [],
    userId,
}) => {
    const { actions, translate } = useContext(ApplicationContext);
    const [showAll, setShowAll] = useState(false);
    const hasPublicationsHistory = publicationHistory.length > 0;
    const slicedPublications = showAll ? publicationHistory : publicationHistory.slice(0, 2);

    const headlineActions = canUserAddPublicationHistory && is.nonEmptyString(userId) && (
        <AddButton onClick={() => actions.navigateTo(createUserPublicationHistoryUrl(userId))} />
    );

    const editAction = (itemId: string): VoidFunction | undefined =>
        canUserEditPublicationHistory && is.nonEmptyString(userId)
            ? () => actions.navigateTo(editUserPublicationHistoryUrl(userId, itemId))
            : undefined;

    useScrollToLocationHash();

    return (
        // biome-ignore lint/complexity/noUselessFragments: suppressed due to migration
        <Fragment>
            <HistoryItemListWrapper
                anchorId="publication-history"
                headline={translate('pages.recruiter-profile.background.publication-history.title')}
                headlineActions={headlineActions}
            >
                {hasPublicationsHistory ? (
                    slicedPublications.map((publicationItem) => (
                        <HistoryItem
                            key={publicationItem.id}
                            caption={<PublicationHistoryCaption />}
                            dateTo={publicationItem.date}
                            description={publicationItem.description}
                            skills={publicationItem.skills}
                            skillsHeadline={translate('pages.user-publication-history.skills-list-headline')}
                            subTitle={[publicationItem.publisher]}
                            title={publicationItem.publicationName}
                            onClickEdit={editAction(publicationItem.id)}
                        />
                    ))
                ) : (
                    <CardPlaceholder
                        placeholderIcon={<ArticleRoundedIcon />}
                        placeholderText={translate(
                            'pages.recruiter-profile.background.publication-history.placeholder',
                        )}
                    />
                )}
                {publicationHistory.length > 2 && (
                    <ShowAllButton showAll={showAll} onClick={() => setShowAll(!showAll)} />
                )}
            </HistoryItemListWrapper>
        </Fragment>
    );
};

type PublicationHistoryProps = {
    readonly userId: string;
};

export const PublicationHistory: FunctionComponent<PublicationHistoryProps> = ({ userId }: PublicationHistoryProps) => {
    const { data: userHistoryOptions, isFetching: userHistoryOptionsIsFetching } = useUserHistoryOptionsQuery(userId);
    const { data: publicationHistory, isFetching: publicationHistoryIsFetching } = usePublicationHistoryQuery(
        userId,
        userHistoryOptions?.canReadHistory ?? false,
    );

    if (userHistoryOptionsIsFetching || publicationHistoryIsFetching) {
        return <InlineLoadingSpinner />;
    }

    return (
        <PublicationHistoryContent
            canUserAddPublicationHistory={userHistoryOptions?.canEditHistory}
            canUserEditPublicationHistory={userHistoryOptions?.canEditHistory}
            publicationHistory={publicationHistory}
            userId={userId}
        />
    );
};
